<template>
  <div ref="timepickerRef" class="relative max-w-[880px]" @click="openDropdown">
    <label
      v-if="label"
      for="time-input-group"
      class="block mb-2 text-md font-medium text-gray-900 dark:text-white pl-1"
    >
      {{ label }}
    </label>

    <div class="relative">
      <label
        for="time-input-group"
        class="absolute transition-all duration-200 ease-in-out pointer-events-none"
        :class="{
          'text-xs top-[calc(0.25rem+3px)] left-4 text-blue-600':
            isFocused || formattedTime,
          'text-gray-500 dark:text-gray-300 text-md top-1/2 left-4 transform -translate-y-1/2':
            !(isFocused || formattedTime)
        }"
        style="left: 1rem"
      >
        {{ placeholder }}
      </label>

      <input
        id="time-input-group"
        v-model="timeInput"
        type="text"
        class="bg-white border border-gray-300 text-md text-gray-900 rounded-md block w-full pl-4 pr-4 pt-6 pb-3 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white focus:ring-blue-600 focus:border-blue-600"
        :class="{ 'ring-1 ring-blue-600 border-blue-600': isDropdownOpen }"
        :style="{ height: '42px' }"
        maxlength="8"
        @input="handleTimeInputChange"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>

    <div
      v-if="isDropdownOpen"
      id="timeDropdown"
      class="absolute left-0 top-full mt-2 bg-white rounded-md shadow-lg border border-gray-300 z-30 p-0 m-0 pt-1"
      @click.stop
    >
      <div class="flex">
        <div class="w-24 max-h-56 overflow-y-auto custom-scrollbar">
          <p
            class="px-4 py-3 font-semibold text-gray-900 sticky top-0 bg-white"
          >
            Hour
          </p>
          <ul class="py-0.5 text-sm text-gray-700">
            <li
              v-for="hour in hours"
              :key="hour"
              :class="{
                'bg-blue-100 border-l-2 border-blue-600': selectedHour === hour,
                'hover:bg-gray-100': selectedHour !== hour
              }"
              class="px-4 py-1 cursor-pointer"
              @click="selectHour(hour)"
            >
              {{ String(hour).padStart(2, "0") }}
            </li>
          </ul>
        </div>
        <div class="w-px bg-gray-300"></div>
        <div class="w-24 max-h-56 overflow-y-auto custom-scrollbar">
          <p
            class="px-4 py-3 font-semibold text-gray-900 sticky top-0 bg-white"
          >
            Minute
          </p>
          <ul class="py-0.5 text-sm text-gray-700">
            <li
              v-for="minute in minutes"
              :key="minute"
              :class="{
                'bg-blue-100 border-l-2 border-blue-600':
                  selectedMinute === minute,
                'hover:bg-gray-100': selectedMinute !== minute
              }"
              class="px-4 py-1 cursor-pointer"
              @click="selectMinute(minute)"
            >
              {{ String(minute).padStart(2, "0") }}
            </li>
          </ul>
        </div>
        <div class="w-px bg-gray-300"></div>
        <div class="w-24 max-h-56 overflow-y-auto custom-scrollbar">
          <p
            class="px-4 py-3 font-semibold text-gray-900 sticky top-0 bg-white"
          >
            Period
          </p>
          <ul class="py-0.5 text-sm text-gray-700">
            <li
              :class="{
                'bg-blue-100 border-l-2 border-blue-600': period === 'AM',
                'hover:bg-gray-100': period !== 'AM'
              }"
              class="px-4 py-1 cursor-pointer"
              @click="selectPeriod('AM')"
            >
              AM
            </li>
            <li
              :class="{
                'bg-blue-100 border-l-2 border-blue-600': period === 'PM',
                'hover:bg-gray-100': period !== 'PM'
              }"
              class="px-4 py-1 cursor-pointer"
              @click="selectPeriod('PM')"
            >
              PM
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    label?: string;
    placeholder?: string;
    minuteStep?: number;
    modelValue?: string;
  }>(),
  {
    label: "",
    placeholder: "Time",
    minuteStep: 15,
    value: ""
  }
);

const emit = defineEmits(["update:modelValue"]);

const timepickerRef = ref<HTMLElement | null>(null);
const timeInput = ref("");
const selectedHour = ref<number | null>(null);
const selectedMinute = ref<number | null>(null);
const period = ref<"AM" | "PM" | null>(null);
const isDropdownOpen = ref(false);
const isFocused = ref(false);

const hours = computed(() => Array.from({ length: 12 }, (_, i) => i + 1));
const minutes = computed(() => {
  const steps: number[] = [];
  for (let i = 0; i < 60; i += props.minuteStep) {
    steps.push(i);
  }
  return steps;
});

const formattedTime = computed(() => timeInput.value);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  if (props.modelValue) {
    parseValue(props.modelValue);
    updateTimeInput();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

function handleFocus() {
  isFocused.value = true;
  isDropdownOpen.value = true;
}

function handleBlur() {
  isFocused.value = false;
  if (selectedHour.value !== null && selectedMinute.value !== null) {
    updateTimeInput();
  }
}

function handleTimeInputChange(event: Event) {
  let value = (event.target as HTMLInputElement).value;

  if (
    value.length === 2 &&
    timeInput.value.length === 1 &&
    /^\d{2}$/.test(value)
  ) {
    value += ":";
  }

  const fourDigitMatch = value.match(/^(\d{4})(.*)$/);
  if (fourDigitMatch && !value.includes(":")) {
    const digits = fourDigitMatch[1];
    const rest = fourDigitMatch[2];
    value = `${digits.slice(0, 2)}:${digits.slice(2)}${rest}`;
  }

  timeInput.value = value;

  parseValue(value);
}

function openDropdown() {
  if (!isDropdownOpen.value) {
    isDropdownOpen.value = true;
    isFocused.value = true;
  }
}

function selectHour(hour: number) {
  selectedHour.value = hour;
  if (selectedMinute.value === null) {
    selectedMinute.value = 0; // Default to 00 minutes when an hour is picked
  }
  updateTimeInput();
}

function selectMinute(minute: number) {
  selectedMinute.value = minute;
  if (selectedHour.value === null) {
    const currentHour = new Date().getHours();
    selectedHour.value = currentHour % 12 || 12;
  }
  updateTimeInput();
}

function selectPeriod(p: "AM" | "PM") {
  period.value = p;
  updateTimeInput();
  isDropdownOpen.value = false;
}

function handleClickOutside(event: MouseEvent) {
  if (
    timepickerRef.value &&
    !timepickerRef.value.contains(event.target as Node)
  ) {
    isDropdownOpen.value = false;
    isFocused.value = false;
  }
}

function updateTimeInput() {
  if (selectedHour.value !== null && selectedMinute.value !== null) {
    const hourStr = String(selectedHour.value).padStart(2, "0");
    const minuteStr = String(selectedMinute.value).padStart(2, "0");
    let timeString = `${hourStr}:${minuteStr}`;
    if (period.value) {
      timeString += ` ${period.value}`;
    }
    timeInput.value = timeString;
  }
}

function parseValue(timeValue: string) {
  const timeRegex = /(\d{1,2})[:.]?(\d{2})?\s*(a|p)?m?/i;
  const match = timeValue.match(timeRegex);

  if (match) {
    const [, hourStr, minuteStr, periodChar] = match;

    if (hourStr) {
      const hour = parseInt(hourStr, 10);
      if (hour >= 1 && hour <= 12) {
        selectedHour.value = hour;
      } else {
        selectedHour.value = null;
      }
    } else {
      selectedHour.value = null;
    }

    if (minuteStr) {
      const minute = parseInt(minuteStr, 10);
      if (minute >= 0 && minute <= 59) {
        selectedMinute.value = minute;
      } else {
        selectedMinute.value = null;
      }
    } else {
      selectedMinute.value = null;
    }

    if (periodChar?.toLowerCase() === "a") {
      period.value = "AM";
    } else if (periodChar?.toLowerCase() === "p") {
      period.value = "PM";
    }
  } else {
    selectedHour.value = null;
    selectedMinute.value = null;
  }
}

watch(timeInput, (newValue, oldVal) => {
  if (newValue !== oldVal) {
    emit("update:modelValue", newValue);
  }
});
</script>

<style scoped>
/* Reusing styles from the date picker for consistency */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 10px;
}

.custom-scrollbar ul {
  white-space: nowrap;
  margin: 0;
  padding: 0;
}

.custom-scrollbar li {
  display: flex;
  align-items: center;
  padding: 0.25rem 1rem;
  height: 2rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.w-24 {
  min-width: 96px;
}

#timeDropdown p {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
  padding: 0.5rem 1rem;
  font-weight: 600;
  color: #4b5563;
}
</style>
